import type { SourcePosition, ScadError } from './errors';

export enum TokenType {
  Number = 'Number',
  String = 'String',
  Boolean = 'Boolean',
  Identifier = 'Identifier',
  // Punctuation
  LParen = '(',
  RParen = ')',
  LBrace = '{',
  RBrace = '}',
  LBracket = '[',
  RBracket = ']',
  Semicolon = ';',
  Comma = ',',
  Colon = ':',
  Equals = '=',
  Hash = '#',
  QuestionMark = '?',
  // Keywords
  For = 'for',
  If = 'if',
  Else = 'else',
  Function = 'function',
  Let = 'let',
  Each = 'each',
  // Operators
  Plus = '+',
  Minus = '-',
  Star = '*',
  Slash = '/',
  Percent = '%',
  Caret = '^',
  // Comparison
  Less = '<',
  Greater = '>',
  LessEq = '<=',
  GreaterEq = '>=',
  EqualEqual = '==',
  BangEqual = '!=',
  // Logical
  Bang = '!',
  And = '&&',
  Or = '||',
  Dot = '.',
  // Special
  EOF = 'EOF',
}

export interface Token {
  type: TokenType;
  value: string;
  from: SourcePosition;
  to: SourcePosition;
}

const KEYWORDS: Record<string, TokenType> = {
  true: TokenType.Boolean,
  false: TokenType.Boolean,
  for: TokenType.For,
  if: TokenType.If,
  else: TokenType.Else,
  function: TokenType.Function,
  let: TokenType.Let,
  each: TokenType.Each,
};

export function tokenize(source: string): { tokens: Token[]; errors: ScadError[] } {
  const tokens: Token[] = [];
  const errors: ScadError[] = [];
  let pos = 0;
  let line = 1;
  let col = 1;

  function here(): SourcePosition {
    return { line, col, offset: pos };
  }

  function peek(): string {
    return pos < source.length ? source[pos] : '\0';
  }

  function peekNext(): string {
    return pos + 1 < source.length ? source[pos + 1] : '\0';
  }

  function advance(): string {
    const ch = source[pos];
    pos++;
    if (ch === '\n') {
      line++;
      col = 1;
    } else {
      col++;
    }
    return ch;
  }

  function skipWhitespace(): void {
    while (pos < source.length) {
      const ch = peek();
      if (ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n') {
        advance();
      } else if (ch === '/' && peekNext() === '/') {
        // Line comment
        while (pos < source.length && peek() !== '\n') advance();
      } else if (ch === '/' && peekNext() === '*') {
        // Block comment
        const from = here();
        advance(); advance(); // skip /*
        let closed = false;
        while (pos < source.length) {
          if (peek() === '*' && peekNext() === '/') {
            advance(); advance();
            closed = true;
            break;
          }
          advance();
        }
        if (!closed) {
          errors.push({
            message: 'Unterminated block comment',
            from,
            to: here(),
            severity: 'error',
          });
        }
      } else {
        break;
      }
    }
  }

  function readNumber(): Token {
    const from = here();
    let value = '';
    while (pos < source.length && (isDigit(peek()) || peek() === '.')) {
      value += advance();
    }
    // Handle scientific notation
    if (pos < source.length && (peek() === 'e' || peek() === 'E')) {
      value += advance();
      if (pos < source.length && (peek() === '+' || peek() === '-')) {
        value += advance();
      }
      while (pos < source.length && isDigit(peek())) {
        value += advance();
      }
    }
    return { type: TokenType.Number, value, from, to: here() };
  }

  function readString(): Token {
    const from = here();
    const quote = advance(); // consume opening quote
    let value = '';
    while (pos < source.length && peek() !== quote) {
      if (peek() === '\\') {
        advance();
        if (pos < source.length) {
          const esc = advance();
          switch (esc) {
            case 'n': value += '\n'; break;
            case 't': value += '\t'; break;
            case '\\': value += '\\'; break;
            case '"': value += '"'; break;
            default: value += esc;
          }
        }
      } else {
        value += advance();
      }
    }
    if (pos < source.length) {
      advance(); // consume closing quote
    } else {
      errors.push({
        message: 'Unterminated string',
        from,
        to: here(),
        severity: 'error',
      });
    }
    return { type: TokenType.String, value, from, to: here() };
  }

  function readIdentifier(): Token {
    const from = here();
    let value = '';
    while (pos < source.length && isIdentChar(peek())) {
      value += advance();
    }
    const type = KEYWORDS[value] ?? TokenType.Identifier;
    return { type, value, from, to: here() };
  }

  function singleToken(type: TokenType): Token {
    const from = here();
    const value = advance();
    return { type, value, from, to: here() };
  }

  function doubleToken(type: TokenType): Token {
    const from = here();
    const v1 = advance();
    const v2 = advance();
    return { type, value: v1 + v2, from, to: here() };
  }

  while (pos < source.length) {
    skipWhitespace();
    if (pos >= source.length) break;

    const ch = peek();

    if (isDigit(ch) || (ch === '.' && isDigit(peekNext()))) {
      tokens.push(readNumber());
    } else if (ch === '"') {
      tokens.push(readString());
    } else if (ch === '$' || isAlpha(ch) || ch === '_') {
      tokens.push(readIdentifier());
    } else if (ch === '(') tokens.push(singleToken(TokenType.LParen));
    else if (ch === ')') tokens.push(singleToken(TokenType.RParen));
    else if (ch === '{') tokens.push(singleToken(TokenType.LBrace));
    else if (ch === '}') tokens.push(singleToken(TokenType.RBrace));
    else if (ch === '[') tokens.push(singleToken(TokenType.LBracket));
    else if (ch === ']') tokens.push(singleToken(TokenType.RBracket));
    else if (ch === ';') tokens.push(singleToken(TokenType.Semicolon));
    else if (ch === ',') tokens.push(singleToken(TokenType.Comma));
    else if (ch === ':') tokens.push(singleToken(TokenType.Colon));
    else if (ch === '#') tokens.push(singleToken(TokenType.Hash));
    else if (ch === '?') tokens.push(singleToken(TokenType.QuestionMark));
    else if (ch === '+') tokens.push(singleToken(TokenType.Plus));
    else if (ch === '-') tokens.push(singleToken(TokenType.Minus));
    else if (ch === '*') tokens.push(singleToken(TokenType.Star));
    else if (ch === '%') tokens.push(singleToken(TokenType.Percent));
    else if (ch === '^') tokens.push(singleToken(TokenType.Caret));
    else if (ch === '/') tokens.push(singleToken(TokenType.Slash));
    else if (ch === '=' && peekNext() === '=') tokens.push(doubleToken(TokenType.EqualEqual));
    else if (ch === '=') tokens.push(singleToken(TokenType.Equals));
    else if (ch === '!' && peekNext() === '=') tokens.push(doubleToken(TokenType.BangEqual));
    else if (ch === '!') tokens.push(singleToken(TokenType.Bang));
    else if (ch === '<' && peekNext() === '=') tokens.push(doubleToken(TokenType.LessEq));
    else if (ch === '<') tokens.push(singleToken(TokenType.Less));
    else if (ch === '>' && peekNext() === '=') tokens.push(doubleToken(TokenType.GreaterEq));
    else if (ch === '>') tokens.push(singleToken(TokenType.Greater));
    else if (ch === '.' && !isDigit(peekNext())) tokens.push(singleToken(TokenType.Dot));
    else if (ch === '&' && peekNext() === '&') tokens.push(doubleToken(TokenType.And));
    else if (ch === '|' && peekNext() === '|') tokens.push(doubleToken(TokenType.Or));
    else {
      const from = here();
      advance();
      errors.push({
        message: `Unexpected character '${ch}'`,
        from,
        to: here(),
        severity: 'error',
      });
    }
  }

  tokens.push({ type: TokenType.EOF, value: '', from: here(), to: here() });
  return { tokens, errors };
}

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function isAlpha(ch: string): boolean {
  return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z');
}

function isIdentChar(ch: string): boolean {
  return isAlpha(ch) || isDigit(ch) || ch === '_' || ch === '$';
}
